/**
 * Sidebar component.
 * Renders a collapsible left-side navigation panel with role-filtered links.
 *
 * Exports:
 *   renderSidebar(activePath, collapsed?) → HTML string
 *   initSidebarListeners() → attaches event listeners
 */

import { getUser, getRole, logout } from '../services/auth.js';
import { navigate } from '../services/router.js';
import { getSupabase } from '../services/supabase.js';

// ── Theme ──────────────────────────────────────────────────────────────────

export function applyTheme(theme) {
  const resolved = theme === 'dark' ? 'dark'
    : theme === 'light' ? 'light'
    : (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  document.documentElement.setAttribute('data-theme', resolved);
  // Store the user's preference (not the resolved value)
  localStorage.setItem('theme', theme || 'auto');
}

// ── Person SVG icon ────────────────────────────────────────────────────────

const PERSON_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="display:block"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>`;

/**
 * Navigation link definitions.
 * Each entry: { label, path, icon, roles }
 * roles: array of roles that can see this link.
 */
const NAV_LINKS = [
  { label: 'Dashboard',       path: '/',               icon: '🏠', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Flights',         path: '/flights',        icon: '📅', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Aircraft',        path: '/aircraft',       icon: '🛩️', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Defects',         path: '/defects',        icon: '⚠️', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Maintenance',     path: '/maintenance',    icon: '🔧', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Billing',         path: '/billing',        icon: '💷', roles: ['pilot', 'admin', 'owner'] },
  { label: 'Settings',        path: '/settings',       icon: '⚙️', roles: ['admin', 'owner'] },
  { label: 'Admin Flights',   path: '/admin/flights',  icon: '📋', roles: ['admin', 'owner'] },
  { label: 'Admin Billing',   path: '/admin/billing',  icon: '💰', roles: ['admin', 'owner'] },
  { label: 'Owner Defects',   path: '/owner/defects',   icon: '🔍', roles: ['owner'] },
  { label: 'Owner Bookings',  path: '/owner/bookings',  icon: '📖', roles: ['owner'] },
];

/**
 * renderSidebar(activePath, collapsed?) → string (HTML)
 *
 * Returns the sidebar HTML with role-filtered navigation links.
 * Brand name "AlliAvia" at top, user email, role badge, logout button.
 *
 * @param {string} activePath - Current route path (e.g. '/', '/billing')
 * @param {boolean} [collapsed=false] - Whether sidebar is in collapsed (icon-only) mode
 * @returns {string} HTML string
 */
export function renderSidebar(activePath, collapsed = false) {
  const user = getUser();
  const role = getRole() || 'pilot';
  const email = user?.email || '';
  const currentTheme = localStorage.getItem('theme') || 'auto';

  const links = NAV_LINKS
    .filter(link => link.roles.includes(role))
    .map(link => {
      const activeClass = link.path === activePath ? ' sidebar__link--active' : '';
      const label = collapsed ? '' : `<span class="sidebar__link-label">${link.label}</span>`;
      return `<a href="#${link.path}" class="sidebar__link${activeClass}" data-path="${link.path}">
        <span class="sidebar__link-icon">${link.icon}</span>${label}
      </a>`;
    })
    .join('\n');

  function themeBtnClass(val) {
    return 'sidebar__theme-btn' + (currentTheme === val ? ' sidebar__theme-btn--active' : '');
  }

  const toolbar = collapsed
    ? `<button class="sidebar__logout" title="Log out">🚪</button>`
    : `<div class="sidebar__toolbar">
        <div class="sidebar__theme-seg" role="group" aria-label="Theme">
          <button class="${themeBtnClass('auto')}"  data-theme-val="auto"  title="System">🖥️</button>
          <button class="${themeBtnClass('light')}" data-theme-val="light" title="Light">☀️</button>
          <button class="${themeBtnClass('dark')}"  data-theme-val="dark"  title="Dark">🌙</button>
        </div>
        <button class="sidebar__logout" title="Log out">🚪</button>
      </div>`;

  return `
    <div class="sidebar__brand" title="${collapsed ? 'Expand' : 'Collapse'} sidebar">
      <img src="/brand/logo3.png" alt="AlliAvia" class="sidebar__brand-logo${collapsed ? ' sidebar__brand-logo--collapsed' : ''}" />
      ${collapsed ? '' : '<span class="sidebar__brand-name">AlliAvia</span>'}
    </div>
    <nav class="sidebar__nav">
      ${links}
    </nav>
    <div class="sidebar__footer">
      <button class="sidebar__user-btn" id="sidebar-user-btn" title="Profile">
        <span class="sidebar__link-icon">${PERSON_ICON}</span>
        ${collapsed ? '' : `<div class="sidebar__user-info">
          <span class="sidebar__user-name" id="sidebar-user-name">${email}</span>
          <span class="sidebar__user-role">${role}</span>
        </div>`}
      </button>
      ${toolbar}
    </div>
  `;
}

/**
 * renderLoginSidebar() → string (HTML)
 *
 * Returns a minimal sidebar for the login page: brand logo + version only.
 * No nav links, no user info, no logout.
 */
export function renderLoginSidebar() {
  return `
    <div class="sidebar__brand">
      <img src="/brand/logo3.png" alt="AlliAvia" class="sidebar__brand-logo" />
      <span class="sidebar__brand-name">AlliAvia</span>
    </div>
    <div class="sidebar__footer sidebar__footer--version">
      <span class="sidebar__version">v1.3</span>
    </div>
  `;
}


/**
 * updateSidebarActiveLink(path)
 *
 * Updates the active nav link without re-rendering the sidebar HTML.
 * Called by the router on each route change.
 *
 * @param {string} path - Current route path (e.g. '/', '/billing')
 */
export function updateSidebarActiveLink(path) {
  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;
  sidebar.querySelectorAll('.sidebar__link').forEach((a) => {
    const isActive = a.dataset.path === path;
    a.classList.toggle('sidebar__link--active', isActive);
  });
}

/**
 * initSidebarListeners()
 *
 * Attaches event listeners for:
 * - Toggle button (expand/collapse on desktop)
 * - Hamburger button (open overlay on mobile)
 * - Close/overlay-backdrop click (close overlay on mobile)
 * - Logout button (calls logout from auth.js, navigates to /login)
 * - Persists collapsed state to localStorage key 'sidebar-collapsed'
 *
 * Uses event delegation on the sidebar element so handlers survive
 * innerHTML replacements. Safe to call multiple times — the delegated
 * listener is attached once via a guard flag.
 */
export function initSidebarListeners() {
  const sidebar = document.getElementById('sidebar');
  if (!sidebar) return;

  // ── Delegated click handler (attached once, survives innerHTML swaps) ──
  if (!sidebar.dataset.delegated) {
    sidebar.dataset.delegated = 'true';

    sidebar.addEventListener('click', async (e) => {
      const target = e.target;

      // Logout button
      if (target.closest('.sidebar__logout')) {
        e.preventDefault();
        try {
          const supabase = getSupabase();
          await logout(supabase);
        } catch (err) {
          console.error('Logout failed:', err);
        }
        navigate('/login');
        return;
      }

      // Brand (logo) — expand/collapse
      if (target.closest('.sidebar__brand')) {
        const isCollapsed = sidebar.classList.toggle('sidebar--collapsed');
        localStorage.setItem('sidebar-collapsed', isCollapsed ? 'true' : 'false');
        const activePath = window.location.hash.slice(1) || '/';
        sidebar.innerHTML = renderSidebar(activePath, isCollapsed);
        // Re-run non-delegated setup (pilot name, etc.)
        initSidebarNonDelegated(sidebar);
        return;
      }

      // Theme buttons
      const themeBtn = target.closest('.sidebar__theme-btn');
      if (themeBtn) {
        const theme = themeBtn.dataset.themeVal;
        applyTheme(theme);
        sidebar.querySelectorAll('.sidebar__theme-btn').forEach((b) => {
          b.classList.toggle('sidebar__theme-btn--active', b.dataset.themeVal === theme);
        });
        return;
      }

      // Profile button
      if (target.closest('#sidebar-user-btn')) {
        const { openProfileModal } = await import('../pages/profile.js');
        openProfileModal();
        return;
      }
    });
  }

  // Hamburger button (outside sidebar element)
  const hamburgerBtn = document.querySelector('.sidebar__hamburger');
  if (hamburgerBtn && !hamburgerBtn._delegated) {
    hamburgerBtn._delegated = true;
    hamburgerBtn.addEventListener('click', () => {
      sidebar.classList.add('sidebar--open');
    });
  }

  // Overlay backdrop click (outside sidebar element)
  const backdrop = document.querySelector('.sidebar__backdrop');
  if (backdrop && !backdrop._delegated) {
    backdrop._delegated = true;
    backdrop.addEventListener('click', () => {
      sidebar.classList.remove('sidebar--open');
    });
  }

  // Non-delegated setup (pilot name load, theme watcher)
  initSidebarNonDelegated(sidebar);
}

/** Attach non-delegated, per-render setup (pilot name, theme watcher). */
function initSidebarNonDelegated(sidebar) {
  // Async: load pilot name into sidebar
  const user = getUser();
  if (user) {
    getSupabase().from('pilots').select('name').eq('id', user.id).single()
      .then(({ data }) => {
        const nameEl = document.getElementById('sidebar-user-name');
        if (nameEl && data?.name) nameEl.textContent = data.name;
      })
      .catch(() => {});
  }

  // Watch system theme changes (for auto mode) — only once
  if (!sidebar._themeWatcher) {
    sidebar._themeWatcher = true;
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if ((localStorage.getItem('theme') || 'auto') === 'auto') {
        applyTheme('auto');
      }
    });
  }
}
